import { regulationLabel } from '@/utils/regulationLabels'

/**
 * The sportsbook-check status under the selected legs: checking, the check
 * error with Retry, or the price line, one row per target regulation and the
 * rejection/unavailable alerts. Reads the current combination only (see
 * usePrepackQuoteTargets).
 */
export default function PrepackQuoteStatus({ quote, legCount, targetRegulations, availableRegulations, controlsDisabled }) {
  const {
    quoteTargetsQuery,
    hasCheckedQuote,
    checkedPreviewTarget,
    recheckingQuote,
    shownTargetMap,
    showRejectedCombination,
    hasUnresolved,
    hasRejectedCombination,
  } = quote
  return (
    <div className="mb-3 mt-2 flex flex-col gap-2">
      {quoteTargetsQuery.isFetching && !hasCheckedQuote ? (
        <p role="status" className="text-xs text-[var(--prepacks-muted)]">
          Checking sportsbook availability…
        </p>
      ) : quoteTargetsQuery.isError ? (
        <div
          role="alert"
          className="rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-3 text-xs text-[var(--wf-color-danger)]"
        >
          <p className="font-semibold">This selection could not be checked.</p>
          <p className="mt-1">
            {quoteTargetsQuery.error?.response?.data?.error ||
              'The sportsbook is unavailable. Try again or change a leg.'}
          </p>
          <button
            type="button"
            disabled={controlsDisabled || quoteTargetsQuery.isFetching}
            onClick={() => void quoteTargetsQuery.refetch()}
            className="mt-2 rounded border border-[var(--wf-color-danger-border)] px-2 py-1 font-semibold hover:bg-[var(--wf-color-danger-soft)] disabled:opacity-50"
          >
            {quoteTargetsQuery.isFetching ? 'Checking…' : 'Retry check'}
          </button>
        </div>
      ) : (
        <div>
          <p role="status" className="text-xs text-[var(--prepacks-muted)]">
            {targetRegulations.length === 0
              ? 'Select at least one target regulation to check this selection.'
              : legCount >= 1 && checkedPreviewTarget?.status === 'valid'
              ? `Sportsbook price: ${checkedPreviewTarget?.pack?.odds ?? '—'}`
              : legCount >= 1
              ? checkedPreviewTarget?.error?.message || 'No price is available for this regulation.'
              : 'Add a leg to request a price.'}
            {recheckingQuote ? ' · Re-checking…' : null}
          </p>

          <div className="mt-2 flex flex-col gap-1.5">
            {targetRegulations.map((regulation) => {
              const target = shownTargetMap.get(regulation)
              const status = target?.status || 'unavailable'
              return (
                <div
                  key={regulation}
                  className={`flex items-center justify-between rounded px-2.5 py-1.5 text-xs ${
                    status === 'valid'
                      ? 'border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] text-[var(--wf-color-text)]'
                      : status === 'invalid'
                      ? 'border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] text-[var(--wf-color-danger)]'
                      : 'border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] text-[var(--prepacks-muted)]'
                  }`}
                >
                  <span className="font-medium">{regulationLabel(regulation, availableRegulations)}</span>
                  <span>
                    {status === 'valid'
                      ? target.pack?.odds || 'Available'
                      : status === 'invalid'
                      ? target.error?.message || 'Rejected combination'
                      : target?.error?.message || 'Unavailable'}
                  </span>
                </div>
              )
            })}
          </div>

          {showRejectedCombination ? (
            <div
              role="alert"
              className="mt-2 rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-3 text-xs text-[var(--wf-color-danger)]"
            >
              <p className="font-semibold">Rejected combination</p>
              <p className="mt-1">Change or remove the rejected leg before publishing.</p>
            </div>
          ) : null}
          {hasUnresolved && !quoteTargetsQuery.isFetching && !quoteTargetsQuery.isError && !hasRejectedCombination ? (
            <div
              role="alert"
              className="mt-2 rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] p-3 text-xs text-[var(--wf-color-danger)]"
            >
              <p className="font-semibold">A target regulation is unavailable</p>
              <p className="mt-1">Remove it or retry the check before publishing.</p>
              <button
                type="button"
                disabled={controlsDisabled}
                onClick={() => void quoteTargetsQuery.refetch()}
                className="mt-2 rounded border border-[var(--wf-color-danger-border)] px-2 py-1 font-semibold hover:bg-[var(--wf-color-danger-soft)] disabled:opacity-50"
              >
                Retry check
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
